'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ChartTooltip } from './chart-tooltip'
import { calculateChartData, type TooltipData } from './chart-utils'
import { ColumnChart } from './column-chart'
import { HorizontalAxis } from './horizontal-axis'
import { LineChart } from './line-chart'
import { VerticalAxis } from './vertical-axis'

interface ChartProps {
    chartType?: 'bar' | 'curve' | 'line'
    data: { x: string | number; y: number }[]
    dataType?: 'number' | 'price'
    height?: number
    labelType?: 'date' | 'number'
    numVerticalTicks?: number
    roundingFactor?: number
    title?: string
    xTickValues: (string | number)[]
}

export function Chart({
    chartType = 'bar',
    data,
    dataType = 'price',
    height: containerHeight = 165,
    labelType = 'date',
    numVerticalTicks = 5,
    roundingFactor = 5,
    title,
    xTickValues,
}: ChartProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(475)
    const [showTooltip, setShowTooltip] = useState(false)
    const [tooltip, setTooltip] = useState<TooltipData>({ xPos: 0, xVal: '', yPos: 0, yVal: 0 })

    useEffect(() => {
        const el = containerRef.current
        if (!el) {
            return
        }

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width)
            }
        })

        observer.observe(el)
        setContainerWidth(el.clientWidth)

        return () => observer.disconnect()
    }, [])

    const toggleTooltip = useCallback(
        (show: boolean, data: TooltipData | Record<string, never>) => {
            setShowTooltip(show)
            if (show && 'xPos' in data) {
                setTooltip(data as TooltipData)
            }
        },
        [],
    )

    const chartData = useMemo(
        () =>
            data.length > 0
                ? calculateChartData({
                      data,
                      containerWidth,
                      containerHeight,
                      numVerticalTicks,
                      roundingFactor,
                  })
                : null,
        [data, containerWidth, containerHeight, numVerticalTicks, roundingFactor],
    )

    if (!chartData) {
        return null
    }

    const { columnWidth, dataPoints, scalingFactorY, verticalAxisLabels } = chartData
    const isLineGraph = chartType === 'curve' || chartType === 'line'

    return (
        <div className="mb-6 flex flex-col md:mb-9">
            {!!title && (
                <h3 className="mb-3.5 text-center text-xs font-bold tracking-[0.1em] text-[#6c7078] uppercase md:text-left">
                    {title}
                </h3>
            )}
            <div ref={containerRef} className="h-[215px]">
                <svg className="h-[calc(100%+40px)] w-full">
                    <VerticalAxis
                        height={containerHeight}
                        scalingFactor={scalingFactorY}
                        type={dataType}
                        values={verticalAxisLabels}
                        width={containerWidth}
                    />
                    <HorizontalAxis
                        columnWidth={!isLineGraph ? columnWidth : 0}
                        data={dataPoints}
                        height={containerHeight}
                        isLineGraph={isLineGraph}
                        labelType={labelType}
                        values={xTickValues}
                    />
                    {chartType === 'bar' && (
                        <ColumnChart
                            columnWidth={columnWidth}
                            data={dataPoints}
                            height={containerHeight}
                            onHover={toggleTooltip}
                        />
                    )}
                    {isLineGraph && (
                        <LineChart data={dataPoints} onHover={toggleTooltip} type={chartType} />
                    )}
                    {showTooltip && (
                        <ChartTooltip
                            chartType={chartType}
                            containerWidth={containerWidth}
                            data={tooltip}
                            dataType={dataType}
                        />
                    )}
                </svg>
            </div>
        </div>
    )
}
